import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";

import {
  getCurrencyRates,
  convertToBaseCurrency,
  convertToCurrency,
  formatData,
  fetchData,
  postQuery,
} from "../../../services/BackendServices";
import AlertModal from "../../Requirement/components/common/AlerModel";
import {
  Typography,
  MenuItem,
  Select,
  FormControl,
  TextField,
  Button,
  TextareaAutosize,
  Switch,
  FormControlLabel,
} from "@mui/material";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";

const CreateOfferForm = ({
  requirementDetails,
  offerDetails,
  onClose,
  onSuccess,
  filterType, // Add filterType prop
  onDeselectRequirement, // Add callback for deselecting requirement
}) => {
  const { setLoading } = useLoading();

  // Load currency rates when component mounts
  useEffect(() => {
    getCurrencyRates();
  }, []);

  // Active tab state
  const [activeTab, setActiveTab] = useState("commodityDetails");

  // Form state - Initialize with requirement details
  const [formData, setFormData] = useState({
    // Commodity Details - Pre-filled from requirement
    productName: "",
    hsCode: "",
    grade: "",
    variety: "",
    originCountry: "",
    destinationCountry: "",
    productSpecifications: "",
    productID: "",
    // Quantity/Packaging - Pre-filled from requirement
    quantity: "",
    packagingType: "Bags",
    packagingSize: "50kg",
    additionalPackagingNotes: "",
    // Delivery/Logistic - Pre-filled from requirement
    shippingMethodPreference: "",
    loadingPort: "",
    dischargePort: "",
    inco: "",
    transitTime: "",
    partialDeliveryAllowed: 0,
    // Pricing/Guarantee
    pricePerMT: "",
    paymentTerms: "",
    currency: "INR",
    // Others
    specialInstructionsBuyer: "",
    internalComments: "",
    OfferStatus: "Indicative",
    OfferType: "Manual",
  });

  // Dropdown options
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [packagingOptions, setPackagingOptions] = useState([]);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [incotermsOptions, setIncotermsOptions] = useState([]);
  const [loadingPorts, setLoadingPorts] = useState([]);
  const [dischargePorts, setDischargePorts] = useState([]);
  const [filteredPackageSizes, setFilteredPackageSizes] = useState([]);
  const [uniquePackagingTypes, setUniquePackagingTypes] = useState([]);

  // Modal state for alerts
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: false,
  });

  // Handle modal close
  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  // Currency options
  const currencyOptions = [
    { value: "INR", label: "INR - Indian Rupee" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "EUR", label: "EUR - Euro" },
    { value: "GBP", label: "GBP - British Pound" },
    { value: "JPY", label: "JPY - Japanese Yen" },
    { value: "CNY", label: "CNY - Chinese Yuan" },
  ];

  // Handle tab click
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Load country-specific ports when country is selected and clear existing port selection
    if (field === "originCountry") {
      if (value) {
        loadPortsByCountry(value, "loading");
      } else {
        setLoadingPorts([]);
      }
      // Clear loading port when origin country changes
      setFormData((prev) => ({
        ...prev,
        loadingPort: "",
      }));
    }
    if (field === "destinationCountry") {
      if (value) {
        loadPortsByCountry(value, "discharge");
      } else {
        setDischargePorts([]);
      }
      // Clear discharge port when destination country changes
      setFormData((prev) => ({
        ...prev,
        dischargePort: "",
      }));
    }
  };

  // Load ports by country
  const loadPortsByCountry = async (country, portType) => {
    try {
      const portsData = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 500,
        filterTerms: { Type: "port", Subtype: country },
      });

      if (portsData?.items) {
        if (portType === "loading") {
          setLoadingPorts(portsData.items);
        } else if (portType === "discharge") {
          setDischargePorts(portsData.items);
        }
      }
    } catch (error) {
      console.error(`Error loading ${portType} ports for ${country}:`, error);
    }
  };

  // Handle packaging type change
  const handlePackagingChange = (e) => {
    const selectedType = e.target.value;
    handleFormChange("packagingType", selectedType);

    // Filter package sizes based on selected type using Subtype
    const filteredSizes = packagingOptions.filter(
      (option) => option.Subtype === selectedType
    );
    setFilteredPackageSizes(filteredSizes);

    // Reset package size when type changes
    handleFormChange("packagingSize", "");
  };

  // Handle package size change
  const handlePackageSizeChange = (e) => {
    handleFormChange("packagingSize", e.target.value);
  };

  // Load dropdown options
  const loadDropdownOptions = useCallback(async () => {
    try {
      setLoading(true);

      // Load products
      const productsResponse = await fetchData({
        target: "listProducts",
        rowStart: 0,
        pageSize: 1000,
      });
      if (productsResponse?.items) {
        setProducts(productsResponse.items);
      }

      // Load countries
      const countriesData = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Type: "Country" },
      });
      if (countriesData?.items) {
        setCountries(countriesData.items);
      }

      // Load packaging options
      const packagingData = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { Type: "packaging" },
      });
      if (packagingData?.items) {
        setPackagingOptions(packagingData.items);

        // Get unique packaging types using Subtype
        const uniqueTypes = [
          ...new Set(
            packagingData.items.map((item) => item.Subtype).filter(Boolean)
          ),
        ];
        setUniquePackagingTypes(uniqueTypes);
      }

      // Load payment terms
      const paymentTermsData = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Type: "paymentTerms" },
      });
      if (paymentTermsData?.items) {
        setPaymentTermsOptions(paymentTermsData.items);
      }

      // Load incoterms
      const incotermsData = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Type: "Incoterms" },
      });
      if (incotermsData?.items) {
        setIncotermsOptions(incotermsData.items);
      }

      // Note: Ports will be loaded dynamically based on selected countries
    } catch (error) {
      console.error("Error loading dropdown options:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Failed to load form options. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Pre-fill form with requirement details or offer details
  const prefillFormData = useCallback(() => {
    // If we have offer details, use them for pre-filling
    if (offerDetails && products.length) {
      console.log("Prefilling form with offer details:", offerDetails);
      // Find product details by ProductName from the offer
      const productInfo = products.find(
        (p) =>
          p.ProductName === offerDetails.ProductName &&
          p.Grade === offerDetails.Grade
      );

      setFormData((prev) => ({
        ...prev,
        // Product details from offer
        productName: offerDetails.ProductName || "",
        grade: offerDetails.Grade || "",
        variety: offerDetails.Variety || "",
        productID: productInfo?.ProductID || "",
        hsCode: offerDetails.HSCode || "",
        // Location details from offer
        originCountry: offerDetails.Country || "",
        destinationCountry: offerDetails.DestinationCountry || "",
        loadingPort: offerDetails.LoadingPort || "",
        dischargePort: offerDetails.DischargePort || "",
        // Quantity and packaging from offer
        quantity: offerDetails.Quantity?.toString() || "",
        packagingType: offerDetails.PackageType || "Bags",
        packagingSize: offerDetails.PackageSize || "50kg",
        // Pricing details from offer
        pricePerMT:
          offerDetails.Price ||
          offerDetails.PricePerMT ||
          formatData(
            convertToCurrency(
              offerDetails.BasePrice / offerDetails.Quantity,
              offerDetails.Currency || "INR"
            )
          ) ||
          "",
        currency: offerDetails.Currency || "INR",
        // Terms from offer
        inco: offerDetails.INCO || "",
        paymentTerms: offerDetails.PaymentTerms || "",
        // Additional details from offer
        productSpecifications: offerDetails.ProductSpecifications || "",
        specialInstructionsBuyer: `Created based on Offer #${offerDetails.OfferNumber}`,
        transitTime: offerDetails.TransitTime || "",
        partialDeliveryAllowed: offerDetails.PartialDeliveryAllowed || 0,
      }));

      // Load ports for pre-filled countries
      if (offerDetails.Country) {
        loadPortsByCountry(offerDetails.Country, "loading");
      }
      if (offerDetails.DestinationCountry) {
        loadPortsByCountry(offerDetails.DestinationCountry, "discharge");
      }
      return;
    }

    // Original logic for requirement details
    if (!requirementDetails || !products.length) return;

    // Find product details
    const productInfo = products.find(
      (p) => p.ProductID === requirementDetails.ProductID
    );

    // Parse delivery location for destination and port
    const deliveryLocationParts =
      requirementDetails.DeliveryLocation?.split("~") || [];
    const destination = deliveryLocationParts[0] || "";
    const deliveryPort = deliveryLocationParts[1] || "";

    // Parse packaging details
    let packaging = "";
    if (requirementDetails.ProductPackaging) {
      try {
        const jsonLike = requirementDetails.ProductPackaging.replace(/{|}/g, "")
          .split(",")
          .map((part) => part.split("="))
          .reduce((obj, [key, value]) => {
            obj[key?.trim()] = value ? value.trim() : "";
            return obj;
          }, {});
        packaging = jsonLike.Subtype || jsonLike.item || "";
      } catch (error) {
        packaging = requirementDetails.ProductPackaging;
      }
    }

    setFormData((prev) => ({
      ...prev,
      // Product details
      productName: productInfo?.ProductName || "",
      grade: productInfo?.Grade || "",
      productID: requirementDetails.ProductID || "",
      // Location details
      originCountry: requirementDetails.OriginCountry || "",
      destinationCountry: destination,
      dischargePort: deliveryPort,
      // Quantity and packaging
      quantity: requirementDetails.Quantity?.toString() || "",
      packagingType: packaging || "Bags",
      // Terms
      inco: requirementDetails.Incoterm || "",
      paymentTerms: requirementDetails.PaymentTerms || "",
      // Additional info
      specialInstructionsBuyer: `Created for Requirement #${requirementDetails.RequirementNumber}`,
    }));

    // Load ports for pre-filled countries
    if (requirementDetails.OriginCountry) {
      loadPortsByCountry(requirementDetails.OriginCountry, "loading");
    }
    if (destination) {
      loadPortsByCountry(destination, "discharge");
    }
  }, [requirementDetails, offerDetails, products]);

  // Submit form
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Validate required fields
      if (
        !formData.productName ||
        !formData.grade ||
        !formData.quantity ||
        !formData.pricePerMT
      ) {
        setModal({
          show: true,
          type: "error",
          title: "Validation Error",
          message:
            "Please fill in all required fields: Product, Grade, Quantity, and Price per MT.",
          autoClose: false,
        });
        return;
      }

      // Prepare offer data
      const offerData = {
        ...formData,
      };

      // Set RequirementsID and CreatedFor based on what we're creating from
      if (offerDetails) {
        // Creating offer based on another offer
        offerData.RequirementsID = requirementDetails?.RequirementsID || "";
        offerData.CreatedFor = `Based on Offer #${offerDetails.OfferNumber}`;
        offerData.BasedOnOfferID = offerDetails.OfferID || "";
      } else if (requirementDetails) {
        // Creating offer based on requirement (original logic)
        offerData.RequirementsID = requirementDetails.RequirementsID;
        offerData.CreatedFor = `Requirement #${requirementDetails.RequirementNumber}`;
      }

      console.log("requirementDetails", requirementDetails);
      offerData.ReferenceOffer =
        offerDetails?.ReferenceOffer || offerDetails?.GPOfferID || "";
      if (filterType === "Request for Firm Offers") {
        offerData.OfferStatus = "Firmoffer";
        offerData.RequirementsID = requirementDetails.RequirementsID;
        offerData.OfferFor = requirementDetails?.CreatedByEmailID;
      }
      if (filterType === "Request for BAFO") {
        offerData.OfferStatus = "BAFO";
        offerData.RequirementsID = requirementDetails.RequirementsID;
        offerData.OfferFor = requirementDetails?.CreatedByEmailID;
      }

      console.log("Offer Data:", offerData);

      offerData.RequirementNumber = requirementDetails?.RequirementsID;
      offerData.ProductName = offerDetails?.ProductName;
      offerData.Grade = offerDetails?.Grade;

      const body = {
        target: "generateAutomaticOffers",
        product: offerData.productID,
        selectedPrice: [offerData],
      };

      console.log("Submitting offer with data:", body);

      const response = await postQuery(body);
      console.log("Response received:", response);

      // Check for success in the parsed response
      if (response && response?.items?.success) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: "Offer created successfully!",
          autoClose: false,
        });

        // Call success callback after a delay
        setTimeout(() => {
          // Call the main success callback
          onSuccess && onSuccess();

          // Deselect parent requirement if filterType is unmatched
          if (filterType === "Unmatched" && onDeselectRequirement) {
            onDeselectRequirement();
          }
        }, 2000);
      } else {
        // Handle case where response exists but success is false
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message:
            response?.items?.message ||
            "Failed to create offer. Please try again.",
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("Error creating offer:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Failed to create offer. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadDropdownOptions();
  }, [loadDropdownOptions]);

  // Pre-fill form when products are loaded
  useEffect(() => {
    prefillFormData();
  }, [prefillFormData]);

  return (
    <div className="row">
      <section className="product-details-unmatched">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            
          }}
        >
          <Typography variant="h6"></Typography>
          <div className="actions">
            <Button
              variant="outlined"
              onClick={onClose}
              style={{ marginRight: "10px" }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              className="actions button"
            >
              SUBMIT
            </Button>
          </div>
        </div>

        <AlertModal
          type={modal.type}
          title={modal.title}
          message={modal.message}
          showModal={modal.show}
          onClose={handleModalClose}
          buttonText="OK"
          autoClose={modal.autoClose}
        />

        <div className="tabs">
          <button
            className={activeTab === "commodityDetails" ? "active" : ""}
            onClick={() => handleTabClick("commodityDetails")}
          >
            Commodity Details
          </button>
          <button
            className={activeTab === "specifications" ? "active" : ""}
            onClick={() => handleTabClick("specifications")}
          >
            Specifications
          </button>
          <button
            className={activeTab === "quantityPackaging" ? "active" : ""}
            onClick={() => handleTabClick("quantityPackaging")}
          >
            Quantity/Packaging
          </button>
          <button
            className={activeTab === "deliveryLogistic" ? "active" : ""}
            onClick={() => handleTabClick("deliveryLogistic")}
          >
            Delivery/Logistic
          </button>
          <button
            className={activeTab === "pricing" ? "active" : ""}
            onClick={() => handleTabClick("pricing")}
          >
            Pricing/Guarantee
          </button>
          <button
            className={activeTab === "others" ? "active" : ""}
            onClick={() => handleTabClick("others")}
          >
            Others
          </button>
        </div>

        {activeTab === "commodityDetails" && (
          <div className="tab-content active">
            <div className="row g-3">
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Product *</Typography>
                  <Select
                    value={formData.productName}
                    onChange={(e) =>
                      handleFormChange("productName", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Product
                    </MenuItem>
                    {[...new Set(products.map((p) => p.ProductName))].map(
                      (productName) => (
                        <MenuItem key={productName} value={productName}>
                          {productName}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <Typography variant="body2">HS Code</Typography>
                <TextField
                  value={formData.hsCode}
                  onChange={(e) => handleFormChange("hsCode", e.target.value)}
                  sx={{ backgroundColor: "white", width: "100%" }}
                  placeholder="Enter HS Code"
                />
              </div>
              <div className="col-md-4">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Grade / Quality *</Typography>
                  <Select
                    value={formData.grade}
                    onChange={(e) => handleFormChange("grade", e.target.value)}
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Grade
                    </MenuItem>
                    {products
                      .filter((p) => p.ProductName === formData.productName)
                      .map((product) => (
                        <MenuItem key={product.Grade} value={product.Grade}>
                          {product.Grade}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-4">
                <Typography variant="body2">Variety</Typography>
                <TextField
                  value={formData.variety}
                  onChange={(e) => handleFormChange("variety", e.target.value)}
                  sx={{ backgroundColor: "white", width: "100%" }}
                  placeholder="Enter Variety"
                />
              </div>
              <div className="col-md-4">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Origin Country</Typography>
                  <Select
                    value={formData.originCountry}
                    onChange={(e) =>
                      handleFormChange("originCountry", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Origin Country
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.Value} value={country.Value}>
                        {country.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Destination Country</Typography>
                  <Select
                    value={formData.destinationCountry}
                    onChange={(e) =>
                      handleFormChange("destinationCountry", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Destination Country
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.Value} value={country.Value}>
                        {country.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        )}

        {activeTab === "specifications" && (
          <div className="tab-content active">
            <div className="row g-3">
              <div className="col-md-12">
                <Typography variant="body2">Product Specifications</Typography>
                <TextareaAutosize
                  value={formData.productSpecifications}
                  onChange={(e) =>
                    handleFormChange("productSpecifications", e.target.value)
                  }
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                  placeholder="Enter Product Specifications"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "quantityPackaging" && (
          <div className="tab-content active">
            <div className="row g-3">
              <div className="col-md-6">
                <Typography variant="body2">Quantity (MT) *</Typography>
                <TextField
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleFormChange("quantity", e.target.value)}
                  sx={{ backgroundColor: "white", width: "100%" }}
                  placeholder="Enter Quantity in MT"
                />
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Packaging Type *</Typography>
                  <Select
                    value={formData.packagingType}
                    onChange={handlePackagingChange}
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Packaging Type
                    </MenuItem>
                    {uniquePackagingTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Packaging Size</Typography>
                  <Select
                    value={formData.packagingSize}
                    onChange={handlePackageSizeChange}
                    disabled={!formData.packagingType}
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      {!formData.packagingType
                        ? "Select Packaging Type first"
                        : "Select Packaging Size"}
                    </MenuItem>
                    {filteredPackageSizes.map((size) => (
                      <MenuItem key={size.Value} value={size.Value}>
                        {size.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-12">
                <Typography variant="body2">
                  Additional Packaging Notes
                </Typography>
                <TextareaAutosize
                  value={formData.additionalPackagingNotes}
                  onChange={(e) =>
                    handleFormChange("additionalPackagingNotes", e.target.value)
                  }
                  style={{
                    width: "100%",
                    minHeight: "80px",
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                  placeholder="Enter Additional Packaging Notes"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "deliveryLogistic" && (
          <div className="tab-content active">
            <div className="row g-3">
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Shipping Method</Typography>
                  <Select
                    value={formData.shippingMethodPreference}
                    onChange={(e) =>
                      handleFormChange(
                        "shippingMethodPreference",
                        e.target.value
                      )
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Shipping Method
                    </MenuItem>
                    <MenuItem value="Road">Road</MenuItem>
                    <MenuItem value="Rail">Rail</MenuItem>
                    <MenuItem value="Ocean">Ocean</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Port of Loading</Typography>
                  <Select
                    value={formData.loadingPort}
                    onChange={(e) =>
                      handleFormChange("loadingPort", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                    disabled={!formData.originCountry}
                  >
                    <MenuItem value="" disabled>
                      {formData.originCountry
                        ? "Select Port of Loading"
                        : "Select Origin Country first"}
                    </MenuItem>
                    {loadingPorts.map((port) => (
                      <MenuItem key={port.Value} value={port.Value}>
                        {port.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Port of Destination</Typography>
                  <Select
                    value={formData.dischargePort}
                    onChange={(e) =>
                      handleFormChange("dischargePort", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                    disabled={!formData.destinationCountry}
                  >
                    <MenuItem value="" disabled>
                      {formData.destinationCountry
                        ? "Select Port of Destination"
                        : "Select Destination Country first"}
                    </MenuItem>
                    {dischargePorts.map((port) => (
                      <MenuItem key={port.Value} value={port.Value}>
                        {port.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Incoterms</Typography>
                  <Select
                    value={formData.inco}
                    onChange={(e) => handleFormChange("inco", e.target.value)}
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Incoterms
                    </MenuItem>
                    {incotermsOptions.map((incoterm) => (
                      <MenuItem key={incoterm.Value} value={incoterm.Value}>
                        {incoterm.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-4">
                <Typography variant="body2">Transit Time</Typography>
                <TextField
                  value={formData.transitTime}
                  onChange={(e) =>
                    handleFormChange("transitTime", e.target.value)
                  }
                  sx={{ backgroundColor: "white", width: "100%" }}
                  placeholder="Enter Transit Time"
                />
              </div>
              <div className="col-md-4">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.partialDeliveryAllowed === 1}
                      onChange={(e) =>
                        handleFormChange(
                          "partialDeliveryAllowed",
                          e.target.checked ? 1 : 0
                        )
                      }
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#fd7e14', // Orange for thumb when checked
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#fd7e14', // Orange for track when checked
                        },
                        '& .MuiSwitch-switchBase': {
                          color: '#f0f0f0', // Light gray for thumb when unchecked (optional)
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: '#f0f0f0', // Light gray for track when unchecked (optional)
                        },
                      }}
                    />
                  }
                  label="Partial Delivery Allowed"
                  sx={{ marginTop: "24px" }}
                />
              </div>
              <div className="col-md-4"></div>
              <div className="col-md-4"></div>
            </div>
          </div>
        )}

        {activeTab === "pricing" && (
          <div className="tab-content active">
            <div className="row g-3">
              <div className="col-md-6">
                <Typography variant="body2">Price per MT *</Typography>
                <TextField
                  type="number"
                  value={formData.pricePerMT}
                  onChange={(e) =>
                    handleFormChange("pricePerMT", e.target.value)
                  }
                  sx={{ backgroundColor: "white", width: "100%" }}
                  placeholder="Enter Price per MT"
                />
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Currency *</Typography>
                  <Select
                    value={formData.currency}
                    onChange={(e) =>
                      handleFormChange("currency", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Currency
                    </MenuItem>
                    {currencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl sx={{ minWidth: "100%" }}>
                  <Typography variant="body2">Payment Terms</Typography>
                  <Select
                    value={formData.paymentTerms}
                    onChange={(e) =>
                      handleFormChange("paymentTerms", e.target.value)
                    }
                    displayEmpty
                    sx={{ backgroundColor: "white" }}
                  >
                    <MenuItem value="" disabled>
                      Select Payment Terms
                    </MenuItem>
                    {paymentTermsOptions.map((paymentTerm) => (
                      <MenuItem
                        key={paymentTerm.Value}
                        value={paymentTerm.Value}
                      >
                        {paymentTerm.Value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        )}

        {activeTab === "others" && (
          <div className="tab-content active">
            <div className="row g-3">
              <div className="col-md-12">
                <Typography variant="body2">
                  Special Instructions for Buyer
                </Typography>
                <TextareaAutosize
                  value={formData.specialInstructionsBuyer}
                  onChange={(e) =>
                    handleFormChange("specialInstructionsBuyer", e.target.value)
                  }
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                  placeholder="Enter Special Instructions for Buyer"
                />
              </div>
              <div className="col-md-12">
                <Typography variant="body2">Internal Comments</Typography>
                <TextareaAutosize
                  value={formData.internalComments}
                  onChange={(e) =>
                    handleFormChange("internalComments", e.target.value)
                  }
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                  placeholder="Enter Internal Comments"
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CreateOfferForm;
